import { action, computed, observable, makeObservable } from 'mobx';

import { UserOnlineStatus } from '@gem/models/userModel';
import {
  IBotUserDisplayData,
  IUserBlockStatus,
  IUserDisplayData,
  IUserPrivacyStatus,
  UserProfileUsernameBadge,
} from '@gem/models/userModel';
import { parsePhone } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { TAvatarBackgroundColorIndex } from 'APP/components/Avatar/Avatar';
import { IUpdateUserDisplayDataB2B } from 'APP/model/userSettings/userSettingsModel.types';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { UsersStore } from 'STORE/Users/Users';

export class User {
  users: UsersStore | null = null;

  @observable id = '';
  @observable phone: string | null = null;
  @observable userName = '';
  @observable nickName = '';
  @observable contactName: string | null = null;
  @observable avatarUrl: string | null = null;
  @observable blockedByMe: IUserBlockStatus | null = null;
  @observable blockedMe: IUserBlockStatus | null = null;
  @observable privacyStatus: IUserPrivacyStatus | null = null;
  @observable usernameBadge: UserProfileUsernameBadge | null = null;
  @observable isUnknown = false;
  @observable verified = false;
  @observable isLoadingBlockedByMe = false;
  @observable deepLink = '';
  @observable bio = '';
  @observable position = '';
  @observable email = '';
  @observable firstName = '';
  @observable lastName = '';
  @observable middleName = '';
  @observable userDisplayName = '';

  constructor(users: UsersStore, data: IUserDisplayData | IBotUserDisplayData) {
    makeObservable(this);
    this.users = users;
    this.id = data.id;
    this.phone = data.phone || null;
    this.userName = data.userName;
    this.nickName = data.nickName;
    this.contactName = data.contactName || null;
    this.avatarUrl = data.avatarUrl || null;
    this.blockedByMe = data.blockedByMe;
    this.blockedMe = data.blockedMe;
    this.privacyStatus = data.privacyStatus;
    this.usernameBadge = data.usernameBadge || null;
    this.verified = data.verified;
    this.isUnknown = data.isUnknown;
    this.deepLink = data.deepLink;
    this.bio = data.bio || '';
    this.position = data.position || '';
    this.email = data.email || '';
    this.firstName = data.firstName || '';
    this.lastName = data.lastName || '';
    this.middleName = data.middleName || '';
    this.userDisplayName = data.displayName || '';
  }

  @action
  update(data: IUserDisplayData): void {
    this.phone = data.phone || null;
    this.userName = data.userName;
    this.nickName = data.nickName;
    this.avatarUrl = data.avatarUrl || null;
    this.privacyStatus = data.privacyStatus || this.privacyStatus;
    this.verified = data.verified;
    this.usernameBadge = data.usernameBadge || null;
    this.blockedMe = data.blockedMe;
    this.blockedByMe = data.blockedByMe;
    this.bio = data.bio || '';
    this.position = data.position || '';
    this.contactName = data.contactName || null;
    this.firstName = data.firstName || '';
    this.lastName = data.lastName || '';
    this.middleName = data.middleName || '';
    this.userDisplayName = data.displayName || '';
  }

  @action
  updateDisplayData(data: IUpdateUserDisplayDataB2B): void {
    this.nickName = data.nickName;
    this.avatarUrl = data.avatarUrl || null;
    this.position = data.position || '';
    this.phone = data.phone || null;
    this.firstName = data.firstName || '';
    this.lastName = data.lastName || '';
    this.middleName = data.middleName || '';
    this.userDisplayName = data.displayName || '';
  }

  @action
  setIsUnknown(isUnknown: boolean): void {
    this.isUnknown = isUnknown;
  }

  @action
  setBlockedByMe(blockedByMe: IUserBlockStatus): void {
    this.blockedByMe = blockedByMe;
  }

  @action
  setLoadingBlockedByMe(isLoading: boolean): void {
    this.isLoadingBlockedByMe = isLoading;
  }

  get isMe(): boolean {
    return false;
  }

  get isBot(): boolean {
    return false;
  }

  @computed
  get group(): Group | null {
    return Entities.GroupsStore.findGroupByUserId(this.id) || null;
  }

  @computed
  get route(): string {
    return this.group ? this.group.route : '';
  }

  @computed
  get isInPhoneBook(): boolean {
    return this.users!.userContacts.some(({ id }) => id === this.id);
  }

  @computed
  get isBlockedByMe(): boolean {
    return this.isLoadingBlockedByMe || Boolean(this.blockedByMe?.value);
  }

  @computed
  get isBlockedMe(): boolean {
    return Boolean(this.blockedMe?.value);
  }

  @computed
  get isBlocked(): boolean {
    return Boolean(this.isBlockedByMe || this.isBlockedMe);
  }

  @computed
  get avatarTitle(): string {
    return this.displayName;
  }

  @computed
  get avatarSubTitle(): string {
    return this.displayPhone || this.displayNickname;
  }

  @computed
  get avatarColorIndex(): TAvatarBackgroundColorIndex {
    return this.id.toString().at(-1) as TAvatarBackgroundColorIndex;
  }

  @computed
  get nameToSort(): string {
    return this.displayName.toLowerCase();
  }

  @computed
  get displayName(): string {
    return (
      this.userDisplayName ||
      (this.isInPhoneBook && this.contactName) ||
      this.userName ||
      this.displayNickname
    );
  }

  @computed
  get displayNickname(): string {
    return this.nickName ? `@${this.nickName}` : '';
  }

  @computed
  get displayPhone(): string {
    const country = Entities.App.availableCountries.find(({ phonePrefix }) => {
      return `+${this.phone}`.indexOf(phonePrefix) === 0;
    });
    return parsePhone(this.phone, country?.phoneTemplate);
  }

  @computed
  get displayPhoneOrNickname(): string {
    return this.displayPhone || this.displayNickname;
  }

  get canAddToGroupMe(): boolean {
    return this.privacyStatus!.addToGroup;
  }

  @computed
  get canAddToPublicMe(): boolean {
    return this.privacyStatus!.addToPublic;
  }

  @computed
  get isOnline(): boolean {
    if (this.isBlocked) {
      return false;
    }

    const onlineStatus = this.users!.getOnlineStatusByUserId(this.id);

    if (!onlineStatus) {
      return false;
    }

    return onlineStatus.status === UserOnlineStatus.Online;
  }

  @computed
  get lastOnlineTs(): number | null {
    const onlineStatus = this.users!.getOnlineStatusByUserId(this.id);

    if (!onlineStatus) {
      return null;
    }

    return parseInt(onlineStatus.lastOnlineTs, 10);
  }

  startChat = (): Promise<Group | null> => Tasks.group.createP2PGroup(this.id);
}
